import React, { useContext, useMemo } from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { useAccountsNav } from "../../utils/moduleNav";

/* ============================================================================
 * Pawn skin for the shared Accounts screens
 * ----------------------------------------------------------------------------
 * The Accounts module was built for the Travel ERP (cyan chips, elevated
 * Paper, purple tree rows). When those same screens are opened from the pawn
 * sidebar they have to read as part of the pawn module instead: white surfaces,
 * slate borders, one blue accent, flat cards, compact type.
 *
 * Rather than fork 2,000 lines of markup, the whole subtree is wrapped in a
 * MUI theme whose component defaults ARE the pawn design tokens — every Card,
 * Button, TextField, Table and Tab inside picks the new look up automatically.
 * The page header (the strongest visual signature) is replaced outright by
 * <PawnAccountsHeader/>.
 * ========================================================================== */

// ─── Blue/White design tokens (same set the pawn screens share) ──────────────
export const PAWN = {
  BLUE: "#2563EB",
  BLUE_DARK: "#1D4ED8",
  BLUE_PALE: "#EFF6FF",
  BLUE_INK: "#1E40AF",
  INK: "#0F172A",
  INK_SOFT: "#334155",
  INK_MUTED: "#64748B",
  INK_FAINT: "#94A3B8",
  BORDER: "#E2E8F0",
  SURFACE: "#FFFFFF",
  PAGE_BG: "#F1F5F9",
  HEAD_BG: "#F8FAFC",
};

// Flat, bordered card — the pawn module's only surface treatment.
export const pawnCardSx = {
  bgcolor: PAWN.SURFACE,
  border: `1px solid ${PAWN.BORDER}`,
  borderRadius: 2.5,
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

// Thin slate scrollbar used by every scrolling pane in the pawn module.
export const pawnScrollSx = {
  "&::-webkit-scrollbar": { width: 6, height: 6 },
  "&::-webkit-scrollbar-track": { background: PAWN.HEAD_BG },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: 3,
    "&:hover": { background: PAWN.INK_FAINT },
  },
};

/**
 * Theme overrides applied to everything rendered under <PawnAccountsSkin/>.
 * Only defaults are touched — any component that sets its own sx still wins,
 * so no existing screen behaviour changes.
 */
const buildPawnTheme = (base) =>
  createTheme(base, {
    palette: {
      primary: {
        main: PAWN.BLUE,
        dark: PAWN.BLUE_DARK,
        light: PAWN.BLUE_PALE,
        contrastText: "#FFFFFF",
      },
      background: { default: PAWN.PAGE_BG, paper: PAWN.SURFACE },
      text: { primary: PAWN.INK, secondary: PAWN.INK_MUTED },
      divider: PAWN.BORDER,
    },
    shape: { borderRadius: 10 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          // Elevated Paper is a Travel-ERP idiom; the pawn module is flat.
          elevation1: { boxShadow: "0 1px 2px rgba(15,23,42,0.04)" },
          elevation2: { boxShadow: "0 1px 2px rgba(15,23,42,0.04)" },
          elevation3: { boxShadow: "0 1px 2px rgba(15,23,42,0.04)" },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${PAWN.BORDER}`,
            borderRadius: 12,
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: "none", fontWeight: 700, borderRadius: 8 },
          containedPrimary: {
            boxShadow: "0 4px 12px rgba(37,99,235,0.22)",
            "&:hover": { boxShadow: "0 4px 14px rgba(37,99,235,0.3)" },
          },
          outlinedPrimary: {
            borderColor: PAWN.BORDER,
            color: PAWN.BLUE_INK,
            backgroundColor: PAWN.SURFACE,
            "&:hover": {
              borderColor: PAWN.BLUE,
              backgroundColor: PAWN.BLUE_PALE,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 700,
            color: PAWN.INK_MUTED,
            borderColor: PAWN.BORDER,
            "&.Mui-selected": {
              backgroundColor: PAWN.BLUE,
              color: "#fff",
              "&:hover": { backgroundColor: PAWN.BLUE_DARK },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8, backgroundColor: PAWN.SURFACE },
          notchedOutline: { borderColor: PAWN.BORDER },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: PAWN.INK_MUTED } } },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 3, borderRadius: 3, backgroundColor: PAWN.BLUE },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.82rem",
            minHeight: 46,
            color: PAWN.INK_MUTED,
            "&.Mui-selected": { color: PAWN.BLUE_INK },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${PAWN.BORDER}`,
            fontSize: "0.8rem",
            color: PAWN.INK_SOFT,
          },
          head: {
            backgroundColor: PAWN.HEAD_BG,
            color: PAWN.INK,
            fontWeight: 800,
            fontSize: "0.72rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          },
        },
      },
      MuiTableContainer: { styleOverrides: { root: pawnScrollSx } },
      MuiChip: { styleOverrides: { root: { fontWeight: 700 } } },
      MuiAccordion: {
        defaultProps: { elevation: 0, disableGutters: true },
        styleOverrides: {
          root: {
            border: `1px solid ${PAWN.BORDER}`,
            borderRadius: 10,
            "&:before": { display: "none" },
          },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 14 } },
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: PAWN.INK, fontSize: "0.7rem" },
        },
      },
    },
  });

/**
 * Wraps an Accounts screen in the pawn look. Pass `active={false}` (Daily Tick
 * / Travel hosts) and it renders its children untouched.
 */
export const PawnAccountsSkin = ({ active = true, children, sx }) => {
  const base = useTheme();
  const pawnTheme = useMemo(() => buildPawnTheme(base), [base]);

  if (!active) return <>{children}</>;

  return (
    <ThemeProvider theme={pawnTheme}>
      <Box
        className="pawn-accounts"
        sx={{
          minHeight: "100%",
          bgcolor: PAWN.PAGE_BG,
          color: PAWN.INK,
          ...sx,
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  );
};

// Square icon button used for back / home, matching the pawn chip styling.
const HeaderIconButton = ({ title, onClick, filled, children }) => (
  <Tooltip title={title} arrow>
    <Box
      onClick={onClick}
      role="button"
      aria-label={title}
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        ...(filled
          ? {
              bgcolor: PAWN.BLUE,
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.32)",
              "&:hover": { bgcolor: PAWN.BLUE_DARK },
            }
          : {
              bgcolor: PAWN.SURFACE,
              color: PAWN.INK_MUTED,
              border: `1px solid ${PAWN.BORDER}`,
              "&:hover": {
                bgcolor: PAWN.BLUE_PALE,
                color: PAWN.BLUE,
                borderColor: PAWN.BLUE,
              },
            }),
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

/**
 * The pawn page header: back + home chips, a blue icon tile, the screen title
 * and the store name as a subtitle, with screen actions pinned right. Mirrors
 * the header used by Pawn Reports / Pawn Masters.
 */
export const PawnAccountsHeader = ({
  icon,
  title,
  subtitle,
  actions,
  showBack = true,
}) => {
  const navigate = useNavigate();
  const { goHome } = useAccountsNav();
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};

  const caption = [currentStoreInfo?.name, subtitle]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        gap: 1.5,
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: 0,
          order: { xs: 2, md: 1 },
        }}
      >
        {showBack && (
          <HeaderIconButton title="Back" onClick={() => navigate(-1)}>
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
          </HeaderIconButton>
        )}
        <HeaderIconButton title="Home" onClick={goHome} filled>
          <HomeRoundedIcon sx={{ fontSize: 20 }} />
        </HeaderIconButton>

        {icon && (
          <Box
            sx={{
              width: 42,
              height: 42,
              ml: 0.25,
              borderRadius: 2,
              bgcolor: PAWN.BLUE,
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}

        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontWeight: 800, fontSize: "1.25rem", color: PAWN.INK }}
          >
            {title}
          </Typography>
          {caption && (
            <Typography
              noWrap
              sx={{ fontSize: "0.75rem", color: PAWN.INK_MUTED }}
            >
              {caption}
            </Typography>
          )}
        </Box>
      </Box>

      {actions && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            order: { xs: 1, md: 2 },
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PawnAccountsSkin;
